// SEO-12 · /resources/udise-apaar-record-readiness
//
// EDITORIAL GROUNDING (see docs/marketing/RESOURCES_EDITORIAL_PROCESS.md):
//   • The UDISE+ minimum student profile, the PEN, the APAAR pre-conditions and the common
//     portal-rejection causes are the vetted statements in `backend/app/core/knowledge.py`
//     (`udise_rules`: udise_pen · apaar_rules · udise_quality) — reused, not rewritten.
//   • Product boundary: claims registry I6 + G4 — readiness findings, consent evidence, export
//     and POST-generation APAAR ID import ONLY. Never ID generation, never live UDISE+/Aadhaar/
//     DigiLocker/ABC synchronisation. The article states that boundary in its own voice.
//   • Consent for APAAR is an append-only ledger decision, not a mutable flag —
//     `core/consent.py` (`PURPOSE_APAAR`, symmetric withdrawal).
//   • Aadhaar: last four digits only (`core/dpdp.py` erasure field list; `students.aadhaar_last4`),
//     and I-15/registry I8 — no Aadhaar e-KYC.
//   • Deterministic readiness checks + AI explanation gating — `core/compliance_ai.py` (rulebook
//     is deterministic; the explanation needs enabled Aira scope and credits).
// NO invented statistics, no submission-success rates, no claim of a government integration.

const article = {
  slug: "udise-apaar-record-readiness",
  title: "UDISE+ and APAAR: what a school fixes before submission",
  seoTitle: "UDISE+ and APAAR Record Readiness | Vidyapeeth360",
  description:
    "The student-record gaps that get a UDISE+ submission rejected, what APAAR actually requires first, and how to find both before the portal window opens.",
  topic: "Compliance",
  topicKey: "compliance",
  readingMinutes: 6,
  published: "2026-08-18",
  updated: "2026-08-18",
  standfirst:
    "A UDISE+ submission does not fail because the school did the work badly. It fails because eleven students have a placeholder date of birth, four share an admission number, and nobody looked until the portal was open.",
  moduleLinks: ["student-admission", "document-management", "custom-reports"],
  conversion: {
    to: "/contact",
    label: "Ask how readiness checks run on your records",
    note: "We will walk your own record set, not a demo dataset — and tell you plainly what the product does not do.",
  },
  sections: [
    {
      id: "the-real-problem",
      heading: "The problem is the register, not the portal",
      blocks: [
        {
          t: "p",
          text: "Every student has to be on UDISE+ with a PEN — a Permanent Education Number that follows the child between schools. The submission itself is mechanical. What makes it painful is that the Data Capture Form asks for a complete profile, and a school's register almost never is: it grew over years, across admission clerks, with a rushed migration somewhere in the middle.",
        },
        {
          t: "p",
          text: "So the work is not \"submit UDISE\". The work is to find, months earlier, the small number of records that will be rejected — and to fix them while the family is still reachable and the file is still in the cupboard.",
        },
      ],
    },
    {
      id: "minimum-profile",
      heading: "The minimum student profile",
      blocks: [
        {
          t: "p",
          text: "A record that is missing any of the following is not UDISE-ready, and will come back at submission:",
        },
        {
          t: "ul",
          items: [
            "**Full name**, spelled as it will appear on every later document — a mid-year correction propagates to the transfer certificate, the board record and the APAAR account.",
            "**Gender** and a **valid date of birth**. Check specifically for placeholder dates — 01/01 of the admission year is the one to look for. It passes a completeness check, fails at the portal, and stays invisible until you look for it deliberately.",
            "**Mother's name** and **father's or guardian's name**, both, as separate fields rather than one \"parent\" string.",
            "**Social category**, recorded from what the family declared, not inferred.",
            "**Address** and a **contact mobile number** that still works. Ten digits, Indian format.",
            "**Admission number** and the **current class and section** — the admission number must be unique in your school, which is exactly what breaks when two clerks number in parallel.",
          ],
        },
        {
          t: "p",
          text: "Validate formats, not just presence: a real ISO date, a ten-digit mobile, a six-digit postal code. \"Present but nonsense\" passes a null check and fails a portal.",
        },
      ],
    },
    {
      id: "apaar",
      heading: "APAAR is a consent problem before it is a data problem",
      blocks: [
        {
          t: "p",
          text: "APAAR — the Automated Permanent Academic Account Registry — is described by the Ministry of Education as a lifelong twelve-digit academic ID linked to the Academic Bank of Credits and DigiLocker. A student is ready for it only when the UDISE-core fields above are complete, a UDISE PEN exists, an Aadhaar reference is on file, and parental consent has actually been recorded as received. The official descriptions of both UDISE+ and APAAR are linked at the end of this article.",
        },
        {
          t: "p",
          text: "Two rules follow from that, and both are easy to get wrong in a hurry.",
        },
        {
          t: "ol",
          items: [
            "**Store the Aadhaar reference as the last four digits only.** A school does not need the full number to establish a reference, and holding it creates an obligation nobody asked for. Aadhaar e-KYC is not part of this workflow at all — document upload reviewed by a school administrator is.",
            "**Consent is evidence, not a tick.** A checkbox in a spreadsheet is not something you can produce eighteen months later when a parent asks who agreed to what. Record the decision, when it was made, who made it, and what they were told — and record a withdrawal the same way.",
          ],
        },
        {
          t: "note",
          title: "What no school-software vendor can do for you",
          text: "Generating the APAAR ID happens on the government's side. Nobody can generate one for you, synchronise it live from a school system, or verify Aadhaar on the government's behalf. Treat any claim otherwise as a claim to check. What software can do is get every record ready, hold the consent evidence, and store the ID once the government process has issued it.",
        },
      ],
    },
    {
      id: "sequence",
      heading: "A submission sequence that ends quietly",
      blocks: [
        {
          t: "ol",
          items: [
            "**Run the check early — twice.** Once well before the window opens, when families can still be called, and once a week before you submit. The first run finds the systemic gaps; the second catches the new admissions.",
            "**Sort findings by who can fix them.** Missing category or guardian name is an office task. A wrong date of birth needs the family and usually a document. Duplicate admission numbers are a numbering-policy fix, and they will recur every year until you take numbering away from the counter and give it to one register.",
            "**Collect consent before you need it**, not on the day. A consent chased under deadline pressure is a consent nobody documented properly.",
            "**Fix in the record of truth**, never in the export. A corrected spreadsheet is a submission that passes once and a school register that is still wrong.",
            "**Keep the run.** The list of what was flagged, what was fixed and what was accepted as-is is your evidence next year — and your answer when somebody asks why a record changed.",
          ],
        },
      ],
    },
    {
      id: "in-the-product",
      heading: "How this works in Vidyapeeth360",
      blocks: [
        {
          t: "p",
          text: "The readiness check is deterministic: a rulebook of field-level checks over your own records, grouped by finding type, producing counts and the exact rows to fix. No model decides whether a record is ready — the same input always produces the same finding list, which is what makes it defensible to re-run and to argue with.",
        },
        {
          t: "p",
          text: "Guardian consent for APAAR is written to an append-only ledger: each decision — granted, rejected or withdrawn — is a new row carrying who, when, the purpose, and how it was verified. The current state is simply the latest row, and withdrawal is as easy as granting, because a consent you cannot withdraw is not consent. Nothing is overwritten, so the evidence survives the correction.",
        },
        {
          t: "p",
          text: "Where Aira is enabled, it can explain a finding in plain language — what the rule is, why the record failed it, and what to change. The finding itself, and the count, stay deterministic; the assistant writes the explanation, never the verdict.",
        },
        {
          t: "p",
          text: "The boundary is worth restating because it is where marketing usually overreaches: Vidyapeeth360 prepares records, holds consent evidence, exports the correction work, and stores an APAAR ID after the government process has issued it. It does not generate the ID, does not synchronise with UDISE+ live, and does not verify Aadhaar. Everything else — the [student record](/modules/student-admission), the supporting [documents](/modules/document-management), the [report you export](/modules/custom-reports) — is yours to keep clean.",
        },
        {
          t: "p",
          text: "If you take one thing from this: check in October for a submission you make in December. The records will be no better in December, and the families will be harder to reach.",
        },
      ],
    },
  ],
  sources: [
    {
      label: "APAAR — the official portal (Ministry of Education)",
      href: "https://apaar.education.gov.in/",
      note: "The government's own description of the APAAR / Academic Bank of Credits account.",
    },
    {
      label: "UDISE+ — the official portal (Ministry of Education)",
      href: "https://udiseplus.gov.in/",
      note: "The portal the annual UDISE+ data-capture window runs on.",
    },
  ],
};

export default article;
